import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Tab,
  Tabs,
  Paper,

} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  AccessTime,
  School,
  Science,
  WaterDrop,
  Analytics,
  TrendingUp,
  VideoLibrary,

} from '@mui/icons-material';
import SupportShell from './SupportShell';

interface VideoTutorial {
  id: string;
  title: string;
  description: string;
  duration: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  category: string;
  thumbnail: string; // Custom thumbnail image path
  youtubeUrl: string; // Direct YouTube URL
  topics: string[];
}

const VideoTutorialsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(0);

  const handleBackToSupport = () => {
    navigate('/support');
  };

  const videoCategories = [
    { label: 'All Videos', icon: <VideoLibrary /> },
    { label: 'Getting Started', icon: <School /> },
    { label: 'Water Quality', icon: <WaterDrop /> },
    { label: 'Calculations', icon: <Analytics /> },
    { label: 'Advanced Topics', icon: <Science /> },
  ];

  const videoTutorials: VideoTutorial[] = [
    {
      id: 'intro-001',
      title: 'FlahaFast Introduction & Overview',
      description: 'Complete introduction to FlahaFast platform and its professional hydroponic calculation capabilities.',
      duration: '8:45',
      level: 'Beginner',
      category: 'Getting Started',
      thumbnail: '/assets/images/video-thumbnails/intro-overview.jpg', // Custom thumbnail
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_INTRO_VIDEO_ID', // Replace with your YouTube URL
      topics: ['Platform Overview', 'Navigation', 'Basic Features'],
    },
    {
      id: 'workflow-001',
      title: '5-Stage Workflow Walkthrough',
      description: 'Step-by-step guide through the complete 5-stage nutrient calculation workflow.',
      duration: '12:30',
      level: 'Beginner',
      category: 'Getting Started',
      thumbnail: '/assets/images/video-thumbnails/workflow-walkthrough.jpg',
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_WORKFLOW_VIDEO_ID',
      topics: ['Workflow Stages', 'Validation Gates', 'Progress Tracking'],
    },
    {
      id: 'water-001',
      title: 'Water Quality Analysis Deep Dive',
      description: 'Comprehensive guide to water quality testing, analysis, and profile creation.',
      duration: '15:20',
      level: 'Intermediate',
      category: 'Water Quality',
      thumbnail: '/assets/images/video-thumbnails/water-quality-analysis.jpg',
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_WATER_VIDEO_ID',
      topics: ['EC Testing', 'pH Analysis', 'Element Concentrations', 'Quality Assessment'],
    },
    {
      id: 'calc-001',
      title: 'Advanced Matrix Calculations',
      description: 'Understanding the mathematical foundations behind FlahaFast\'s calculation engine.',
      duration: '18:45',
      level: 'Advanced',
      category: 'Calculations',
      thumbnail: '/assets/images/video-thumbnails/matrix-calculations.jpg',
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_CALC_VIDEO_ID',
      topics: ['Matrix Solving', 'SVD Fallback', 'Precision Tuning', 'Error Analysis'],
    },
    {
      id: 'ph-001',
      title: 'pH Adjustment & Buffer Systems',
      description: 'Master pH calculations using Henderson-Hasselbalch equations and buffer modeling.',
      duration: '14:15',
      level: 'Advanced',
      category: 'Advanced Topics',
      thumbnail: '/assets/images/video-thumbnails/ph-adjustment.jpg',
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_PH_VIDEO_ID',
      topics: ['Henderson-Hasselbalch', 'Buffer Capacity', 'Acid/Base Calculations'],
    },
    {
      id: 'stock-001',
      title: 'Stock Solution Optimization',
      description: 'Professional techniques for optimizing stock solution configurations and preventing precipitation.',
      duration: '16:30',
      level: 'Expert',
      category: 'Advanced Topics',
      thumbnail: '/assets/images/video-thumbnails/stock-solutions.jpg',
      youtubeUrl: 'https://www.youtube.com/watch?v=YOUR_STOCK_VIDEO_ID',
      topics: ['A+B Tank Configuration', 'Precipitation Prevention', 'Concentration Factors'],
    },
  ];

  const filteredVideos = selectedCategory === 0 
    ? videoTutorials 
    : videoTutorials.filter(video => video.category === videoCategories[selectedCategory].label);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner': return 'success';
      case 'Intermediate': return 'info';
      case 'Advanced': return 'warning';
      case 'Expert': return 'error';
      default: return 'default';
    }
  };

  // Simple YouTube redirect function
  const openYouTubeVideo = (video: VideoTutorial) => {
    window.open(video.youtubeUrl, '_blank', 'noopener,noreferrer');
  };



    return (
    <SupportShell
      title="Video tutorials"
      subtitle="Walkthrough media for FlahaFast workflows."
    >
{/* Video Categories */}
      <Box sx={{ mb: 2 }}>
          <Paper sx={{ p: 1 }}>
            <Tabs
              value={selectedCategory}
              onChange={(_, newValue) => setSelectedCategory(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '1rem',
                },
              }}
            >
              {videoCategories.map((category, index) => (
                <Tab
                  key={index}
                  icon={category.icon}
                  label={category.label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Paper>
        </Box>

          <Typography
            variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}
          >
            {videoCategories[selectedCategory].label}
          </Typography>

          <Grid container spacing={4}>
            {filteredVideos.map((video) => (
              <Grid key={video.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'none',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={video.thumbnail}
                      alt={video.title}
                      sx={{
                        cursor: 'pointer',
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                      onClick={() => openYouTubeVideo(video)}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1,
                        },
                      }}
                      onClick={() => openYouTubeVideo(video)}
                    >
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: 'white',
                          },
                        }}
                      >
                        <PlayArrow sx={{ fontSize: 48 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <AccessTime sx={{ fontSize: 16, mr: 0.5 }} />
                    {video.duration}
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                        {video.title}
                      </Typography>
                      <Chip
                        label={video.level}
                        color={getLevelColor(video.level) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {video.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Topics:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {video.topics.map((topic, index) => (
                          <Chip
                            key={index}
                            label={topic}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PlayArrow />}
                      onClick={() => openYouTubeVideo(video)}
                      sx={{ borderRadius: 2 }}
                    >
                      Watch Tutorial
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
    </SupportShell>
  );
};

export default VideoTutorialsPage;
